import { type JanusCtx, type Row, HandBack, actorId, ecId, itemField, record, upper, definitionNotHeld } from '../ability/context.js';
import { ABILITY_CHARGE_BUCKETS, abilityEntryId } from '../ability/charges/charge-entry.js';
import { statusItemId, STATUS_IDS } from './expiry.js';
import { STATUS_REMOVAL_TAIL_IDS } from './gms-tools.js';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { getStatusEffectDurationTimes } from '../ability/statuses/get-status-effect-duration-times.js';
import { empty } from '../php-compat/index.js';
import { layerKeysDeep } from '../ability/movement/check-push-and-pull-after-hit.js';
import { FALLING_DAMAGE_ID } from '../ability/movement/fall-damage.js';
import { statusScalar } from '../actions/activity.js';

const HASTED = '091B2BEE-7F61-D7DB-0CF1-6D255F4B7663', STUNNED = 'D218F4FB-5693-87B9-93C1-776FC1F7ED90';
const TRANSPORTS: ReadonlySet<string> = new Set([STATUS_IDS.riding, STATUS_IDS.piloting, STATUS_IDS.ridden, STATUS_IDS.piloted]);

export function exactRemovalTailDefinitionIds(statuses: readonly Row[], read: JanusCtx['read'], supportHydration: 'eager' | 'on-demand' = 'eager'): string[] {
  const ids = new Set<string>();
  for (const status of statuses) {
    const itemId = statusItemId(status), definition = read.definition(itemId) ?? record(status.status_arr);
    if (itemId === HASTED) ids.add(STUNNED);
    if (supportHydration === 'on-demand') continue;
    const keys = definition ? layerKeysDeep({ read }, definition) : [];
    if (keys?.some(key => /^speed_|^movement_|shapeshift/.test(key))
      || TRANSPORTS.has(itemId)) {
      ids.add(FALLING_DAMAGE_ID); ids.add('F5ACBDB4-D923-3684-57F6-FBF3F720AEFA');
    }
  }
  return [...ids];
}

/** Exact exits own the complete tail. Never persist a removal with an omitted special or temporary pool cleanup. */
export function resolveExactRemovalTails(ctx: JanusCtx, owner: Row, removed: Row, options: { summonTagHandled?: boolean; formHandled?: boolean } = {}): void {
  const id = statusItemId(removed), definition = ctx.read.definition(id) ?? record(removed.status_arr);
  if (!definition) definitionNotHeld('zone-exit:removal-definition-not-held', id);
  const name = String(itemField(definition, 'item_name') ?? id);
  if (id === HASTED) {
    if (!ctx.read.campaignClock?.()) throw new HandBack('zone-exit:hasted-campaign-clock-not-held');
    const duration = { item_layer_types: { effect_ends_on_end_of_caster_next_turn: [1] } };
    applyStatusEffect(ctx, { abilityId: HASTED, casterId: ecId(owner), targetIds: [ecId(owner)], rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' }, definition, STUNNED, 'status_effect_applied', owner, owner,
    getStatusEffectDurationTimes(ctx, duration, owner), true, true, 0, false, upper(ctx.read.value(removed, 'status_origin_id')) || HASTED);
  }
  if ((id !== HASTED && STATUS_REMOVAL_TAIL_IDS.has(id)) || (id === STATUS_IDS.summon_tag && !options.summonTagHandled)
    || String(itemField(definition, 'item_type')) === 'creature' && !options.formHandled
    || ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
    throw new HandBack(`zone-exit:special-tail:${name}`);

  resolveTemporaryChargeRemovals(ctx, owner, definition, removed);
}

/** A temporary grant never owns the holder's independent pool, including a nested grant of that same ability. */
export function resolveTemporaryChargeRemovals(ctx: JanusCtx, owner: Row | null, definition: Row, removed?: Row): void {
  const grants = (owner ? ABILITY_CHARGE_BUCKETS.flatMap(bucket => ctx.read.entries(owner, bucket)) : []).map(entry => {
    const source = ctx.read.value(record(entry), 'source');
    return { id: abilityEntryId(entry), source: upper(record(source) ? ctx.read.value(record(source), 'source') : source) };
  });
  const removedSource = upper(statusScalar(removed ?? {}, 'status_id'));
  const temporarySources = new Set(removedSource ? [removedSource] : []);
  // Compiled grants retain their source: a child of this status is temporary, while
  // another class/item/status grant of the same ability still owns the shared pool.
  for (;;) {
    const prior = temporarySources.size;
    for (const grant of grants) if (grant.id && temporarySources.has(grant.source)
      && grants.filter(other => other.id === grant.id).every(other => temporarySources.has(other.source))) temporarySources.add(grant.id);
    if (temporarySources.size === prior) break;
  }
  const ownAbilities = new Set(grants.filter(grant => !temporarySources.has(grant.source)).map(grant => grant.id));
  const temporary = new Set<string>();
  const visited = new Set<string>();
  const visit = (source: Row, id = upper(itemField(source, 'item_id'))): void => {
    if (id && visited.has(id)) return;
    if (id) visited.add(id);
    if (id && !empty(ctx.read.value(source, 'ability_charges')) && !ownAbilities.has(id)) temporary.add(id);
    for (const bucket of ABILITY_CHARGE_BUCKETS) for (const entry of ctx.read.entries(source, bucket)) {
      const childId = abilityEntryId(entry);
      if (!childId || visited.has(childId)) continue;
      const child = ctx.read.definition(childId) ?? record(record(entry)?.item_arr);
      if (!child) definitionNotHeld('zone-exit:temporary-charge-definition-not-held', childId);
      visit(child, childId);
    }
  };
  visit(definition);
  if (!temporary.size) return;
  if (!owner) throw new HandBack('zone-exit:temporary-charge-holder-not-held');
  for (const itemId of temporary) {
    const removals = ctx.chargesRemoved ??= [];
    if (!removals.some(pool => pool.holder === actorId(owner) && pool.item_id === itemId)) removals.push({ holder: actorId(owner), item_id: itemId });
    if (removals.length > 128) throw new HandBack('zone-exit:temporary-charge-write-limit');
  }
}
